import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from './MetricDeltas'
import { MONO, fmt } from './format'

interface HeroCardProps {
  trueSales: number
  /** Prior month's True Sale, or null if not in the dataset. */
  prevMonth: number | null
  /** Same month last year's True Sale, or null if not in the dataset. */
  prevYear: number | null
  prevYearLabel: string
  netSales: number
  currency: string
  month: string
  /** Consolidated view only — the selected month's USD-per-€1 rate to surface. */
  fxRate?: number
  /** Label for the prior comparison, e.g. "prior month" or "prior period". */
  prevLabel?: string
}

/** The True Sales hero — the single headline number for the selected period. */
export function HeroCard({ trueSales, prevMonth, prevYear, prevYearLabel, netSales, currency, month, fxRate, prevLabel }: HeroCardProps) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-6 dark:border-stone-800 dark:bg-stone-900">
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-champagne-500/10 blur-2xl"
        aria-hidden
      />
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-champagne-500" aria-hidden />
          <p className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">
            True Sales · {month}
            <InfoPopover term="trueSale" />
          </p>
        </div>
        {fxRate != null && (
          <span className="relative inline-flex shrink-0 items-center gap-1 rounded-full bg-stone-100/80 px-2 py-0.5 text-[11px] text-stone-500 dark:bg-stone-800/70 dark:text-stone-400">
            <span className={MONO}>€1 = ${fxRate.toFixed(4)}</span>
            <InfoPopover term="consolidatedFx" />
          </span>
        )}
      </div>
      <p className={cn('mt-3 text-4xl font-semibold tracking-tight sm:text-5xl', MONO)}>
        {fmt(trueSales, currency)}
      </p>
      <MetricDeltas
        current={trueSales}
        prevMonth={prevMonth}
        prevYear={prevYear}
        prevYearLabel={prevYearLabel}
        prevLabel={prevLabel}
        currency={currency}
        size="lg"
      />
      <p className="mt-3 inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400">
        Net Sales{' '}
        <span className={cn('font-medium text-stone-700 dark:text-stone-200', MONO)}>{fmt(netSales, currency)}</span>
        <InfoPopover term="netSale" />
      </p>
      <p className="mt-3 inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-stone-400 dark:text-stone-500">
        <span className="inline-flex items-center gap-1">Revenue + Pipeline (YTD) <InfoPopover term="splitByRecognition" /></span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1">Settled + Outstanding <InfoPopover term="splitByPayment" /></span>
      </p>
    </div>
  )
}
